import React, { useEffect, useState } from 'react'
import { useHistory } from 'react-router-dom'
import Header from '_components/header/index.jsx'
import Form from '_components/form/Form'
import { loadSchema, loadEntity, saveConfig, updateConfig } from '_services/destination-configs'
import { CircularLoader as Loader } from '@st-operations/uikit/src/v2/index.js'
import toast from '@st-operations/uikit/lib/toast'

import { getFieldsForSchemaKind, getSchemaFoSchemaKind } from './utils'
import NewDataFormat from './new-data-format'
import UpdateDataFormat from './update-data-format'

import './styles.scss'

const defaultSegmentConfig = {
  dataFormat: {
    schema: [],
    formatters: [],
  },
  fileFormat: {},
}

const SegmentConfig = (props) => {
  const [segmentConfig, setSegmentConfig] = useState(defaultSegmentConfig)
  const [fileFormatFields, setFileFormatFields] = useState([])
  const [schemaFields, setSchemaFields] = useState([])
  const [formatterFields, setFormatterFields] = useState([])
  const [schema, setSchema] = useState({})
  const [loading, setLoading] = useState(false)

  const history = useHistory()

  useEffect(() => {
    const id = props?.match?.params?.id
    if (id && id !== 'new') loadSegment(id)
    else loadNewSegment()
  }, [])

  const setFields = (schema) => {
    if (schema?.fileFormat?.properties) {
      const ffFields = []
      for (const [, value] of Object.entries(schema.fileFormat.properties)) {
        ffFields.push(value)
      }
      setFileFormatFields(ffFields)
    }
    if (schema?.dataFormat?.schema?.properties) {
      const schemaFields = []
      for (const [, value] of Object.entries(schema?.dataFormat?.schema?.properties)) {
        schemaFields.push(value)
      }
      setSchemaFields(schemaFields)
    }
    if (schema?.dataFormat?.formatters?.properties) {
      const formatterFields = []
      for (const [, value] of Object.entries(schema?.dataFormat?.formatters?.properties)) {
        formatterFields.push(value)
      }
      setFormatterFields(formatterFields)
    }
  }

  const loadNewSegment = async () => {
    try {
      const schema = await loadSchema()
      setFields(schema)
      setSchema(schema)
    } catch (error) {
      toast.error(`Error while loading schema ${error.message}`)
    }
  }

  const loadSegment = async (id) => {
    try {
      const [schema, data] = await Promise.all([loadSchema(), loadEntity(id)])
      setFields(schema)
      setSchema(schema)
      setSegmentConfig(data)
    } catch (error) {
      toast.error(`Error while loading segment config ${error.message}`)
    }
  }

  const update = async () => {
    try {
      setLoading(true)
      const data = await updateConfig(segmentConfig._id, segmentConfig)
      setSegmentConfig(data)
      toast('Successfully updated config')
      setLoading(false)
    } catch (error) {
      toast.error(`Error while updating config ${error.message}`)
      setLoading(false)
    }
  }

  const save = async () => {
    try {
      setLoading(true)
      const data = await saveConfig(segmentConfig)
      setSegmentConfig(data)
      toast('Successfully saved config')
      setLoading(false)
    } catch (error) {
      toast.error(`Error while saving config ${error.message}`)
      setLoading(false)
    }
  }
  const onGeneralPropertyChange = (value, name) => {
    const updatedConfig = {
      ...segmentConfig,
      [name]: value,
    }
    setSegmentConfig(updatedConfig)
  }

  const onFileFormatPropertyChange = (value, name) => {
    const updatedConfig = {
      ...segmentConfig,
      fileFormat: {
        ...segmentConfig.fileFormat,
        [name]: value?.value || value,
      },
    }
    setSegmentConfig(updatedConfig)
  }

  const onDataFormatRecordChange = (index, value, name, type) => {
    const configToUpdate = { ...segmentConfig }
    const recordToEdit = configToUpdate.dataFormat[type][index]
    recordToEdit[name] = value?.value ? value.value : value
    configToUpdate.dataFormat[type][index] = recordToEdit
    setSegmentConfig(configToUpdate)
  }

  const onDataFormatRecordRemove = (index, type) => {
    const configToUpdate = { ...segmentConfig }
    configToUpdate?.dataFormat[type]?.splice(index, 1)
    setSegmentConfig(configToUpdate)
  }

  const addNewRecord = (item, type) => {
    if (Object.keys(item)?.length > 0) {
      const configToUpdate = { ...segmentConfig }
      configToUpdate.dataFormat[type].push(item)
      setSegmentConfig(configToUpdate)
    }
  }

  return (
    <div>
      <Header
        title={segmentConfig?.name || 'New Segment'}
        actions={[
          {
            children: segmentConfig?._id ? 'Update' : 'Save',
            onClick: segmentConfig?._id ? update : save,
          },
          {
            children: 'Cancel',
            onClick: () => {
              history.push('/segment-configs')
            },
          },
        ]}
      />
      <If condition={loading}>
        <div className="audience-creator__loader-container">
          <Loader className="audience-creator__loader" />
        </div>
      </If>
      <Form
        title="Metadata"
        item={segmentConfig}
        schema={schema}
        onChange={onGeneralPropertyChange}
        fields={[{ name: 'name' }, { name: 'version' }]}
      />

      <section className="sg-card">
        <header className="sg-card__header-container">
          <h1 className="sg-card__header">Data Format - Schema</h1>
        </header>
        <div className="sg-card__content">
          <NewDataFormat
            fields={schemaFields}
            schema={schema?.dataFormat?.schema?.properties}
            type="schema"
            onSave={addNewRecord}
          />
          <If condition={segmentConfig?.dataFormat?.schema?.length > 0}>
            <For each="item" index="index" of={segmentConfig.dataFormat.schema}>
              <UpdateDataFormat
                item={item}
                fields={schemaFields?.length > 0 ? getFieldsForSchemaKind(item, schemaFields) : []}
                schema={
                  schema?.dataFormat?.schema?.properties
                    ? getSchemaFoSchemaKind(item, schema?.dataFormat?.schema?.properties)
                    : {}
                }
                index={index}
                type="schema"
                onChange={onDataFormatRecordChange}
                onRemove={onDataFormatRecordRemove}
              />
            </For>
          </If>
        </div>
      </section>
      <section className="sg-card">
        <header className="sg-card__header-container">
          <h1 className="sg-card__header">Data Format - Formatters</h1>
        </header>
        <div className="sg-card__content">
          <NewDataFormat
            fields={formatterFields}
            schema={schema?.dataFormat?.formatters?.properties}
            onSave={addNewRecord}
            type="formatters"
          />

          <If condition={segmentConfig?.dataFormat?.formatters?.length > 0}>
            <For each="item" index="index" of={segmentConfig.dataFormat.formatters}>
              <UpdateDataFormat
                item={item}
                fields={formatterFields}
                schema={schema?.dataFormat?.formatters?.properties}
                index={index}
                type="formatters"
                onChange={onDataFormatRecordChange}
                onRemove={onDataFormatRecordRemove}
              />
            </For>
          </If>
        </div>
      </section>
      <section className="sg-card">
        <header className="sg-card__header-container">
          <h1 className="sg-card__header">File Format</h1>
        </header>
        <div className="sg-card__content">
          <Form
            fields={fileFormatFields}
            schema={schema?.fileFormat?.properties}
            item={segmentConfig?.fileFormat}
            onChange={onFileFormatPropertyChange}
            className="selected-destinations__form"
          />
        </div>
      </section>
    </div>
  )
}

export default SegmentConfig
